import {
    useEffect,
    useRef
} from 'react';

import {Hub} from '@aws-amplify/core';
import {Cache} from '@aws-amplify/cache';
import {useNavigate} from 'react-router-dom';

import {
    getWithIdTokenPayload,
    update as updateAccount
} from '../../accounts/services/AccountsService';
import {ActivityEventsService} from '../../debug-mode/activity-events/services/ActivityEventsService';

/**
 * Listens for Amplify Hub auth events and handles signIn, signIn_failure,
 * and cognitoHostedUI_failure. Returns a ref indicating whether auth completed.
 *
 * The Hub listener is registered inside a useEffect and cleaned up on unmount,
 * preventing multiple listeners from accumulating on re-renders.
 */
export const useAuthEventListener = (isAuthorizing: boolean) => {
    const navigate = useNavigate();
    const authCompletedRef = useRef(false);

    useEffect(() => {
        if (!isAuthorizing) {
            return;
        }

        authCompletedRef.current = false;

        const hubListenerCancelToken = Hub.listen('auth', (data) => {
            const {payload} = data;

            switch (payload.event) {
                case 'signIn':
                    handleSignInEvent(payload);
                    break;
                case 'cognitoHostedUI':
                    ActivityEventsService.add(
                        'SSO Authentication Successful',
                        'Cognito Hosted UI authentication successful',
                        undefined
                    );
                    break;
                case 'signIn_failure':
                    hubListenerCancelToken();
                    authCompletedRef.current = true;
                    ActivityEventsService.add(
                        'Sign-In Failed',
                        'Authentication failed',
                        undefined
                    );
                    Cache.setItem('pac-authorizing', 'no');
                    navigate('/auth-error');
                    break;
                case 'cognitoHostedUI_failure':
                    hubListenerCancelToken();
                    authCompletedRef.current = true;
                    ActivityEventsService.add(
                        'SSO Authentication Failed',
                        'Cognito Hosted UI authentication failed',
                        undefined
                    );
                    Cache.setItem('pac-authorizing', 'no');
                    navigate('/auth-error');
                    break;
            }
        });

        const handleSignInEvent = async (payload: { event: string; data?: any }) => {
            hubListenerCancelToken();
            authCompletedRef.current = true;

            console.debug('LOGIN APP. INDEX PAGE. EVENT', payload.event);

            if (!payload.data?.signInUserSession) {
                console.trace('LOGIN APP. INDEX PAGE. SIGN IN EVENT - No valid session data');
                ActivityEventsService.add(
                    'Sign-In Failed - No Session',
                    'Sign-in event received but session data was missing or null',
                    undefined
                );
                Cache.setItem('pac-authorizing', 'no');
                navigate('/auth-error');
                return;
            }

            console.log('LOGIN APP. INDEX PAGE. PAYLOAD', payload.data);

            let idTokenPayload = payload.data.signInUserSession.idToken.payload;
            console.log('LOGIN APP. INDEX PAGE. ID TOKEN PAYLOAD', JSON.stringify(idTokenPayload, null, 2));

            console.debug('FIND ACCOUNT WITH TOKEN PAYLOAD', idTokenPayload);

            const account = await getWithIdTokenPayload(idTokenPayload);
            console.debug('GET ACCOUNT WITH PAYLOAD RESULT', JSON.stringify(account, null, 2));

            if (account) {
                console.debug('FOUND ACCOUNT IN HOME PAGE', JSON.stringify(account, null, 2));

                ActivityEventsService.add(
                    'User Signed In Successfully',
                    `Completed sign-in for: ${account.emailAddress}`,
                    account.emailAddress
                );

                await updateAccount(
                    account.id,
                    Object.assign({}, account, {
                        isAuthorizing: 'no',
                    })
                );
                navigate(`/accounts/${account.id}/redirect`);
            } else {
                const email = idTokenPayload['email'];

                ActivityEventsService.add(
                    'Sign-In Failed - Account Not Found',
                    `User authenticated but no account profile exists: ${email}`,
                    email
                );

                Cache.setItem('pac-authorizing', 'no');
                navigate(`/accounts/not-found/${encodeURIComponent(email)}`);
            }
        };

        return () => hubListenerCancelToken();
    }, [isAuthorizing, navigate]);

    return authCompletedRef;
};
